import React, { useState, useMemo } from "react";
import authFetch from "../authFetch";
import { getEffectiveTaxonomy, getChildTaxonomy } from "../permissions/checks";

/* ══ Taxonomy Editor ════════════════════════════════════════
   Per-module workspace editor. Renders the effective taxonomy
   (seed + platform + workspace) and lets the owner override
   defaults or add workspace-only entries. Persists via
   /api/tasks?action=save_taxonomy_entry (Phase 1 API).

   Uses the three-layer precedent from permissions/checks.js:
   the same entry ID can exist at multiple layers; workspace
   wins. A "Disable" on a seed/platform row creates a workspace
   override with enabled:false rather than deleting anything.

   Used by Settings.jsx per-module tabs (Batch 4.2). Facility
   renders as a parent → children tree; other modules render
   flat. Expense category picker lands in Batch 4.3.           */

const PRIORITIES = ["low", "medium", "high", "urgent"];

export default function TaxonomyEditor({ state, dispatch, platformCtx, module, lang, t }) {
  const isRTL = lang === "ar";
  const API = typeof window !== "undefined" ? window.location.origin : "";
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showAdd, setShowAdd] = useState(null); /* {parentId} */
  const [addForm, setAddForm] = useState(null);
  /* Expense category sources (Batch 4.3). Workspace expenseCats is
     the canonical picker — it already feeds TaskExpenseModal. Only
     root entries here; sub picker appears once a root is chosen. */
  const expenseCats = (state.expenseCats || []).filter(c => !c.parent);
  const subCatsOf = (catId) => (state.expenseCats || []).filter(c => c.parent === catId);

  const effective = useMemo(
    () => getEffectiveTaxonomy(state, platformCtx, module),
    [state, platformCtx, module]
  );
  /* Include disabled entries so owners can see them and re-enable;
     getEffectiveTaxonomy filters them out by design. */
  const workspaceRows = state.taskTaxonomy || [];
  const allForModule = useMemo(() => {
    const merged = getEffectiveTaxonomy({ ...state, taskTaxonomy: [] }, platformCtx, module);
    const byId = new Map(merged.map(e => [e.id, e]));
    for (const w of workspaceRows) {
      if (w.module === module) byId.set(w.id, { ...(byId.get(w.id) || {}), ...w, source: "workspace" });
    }
    return Array.from(byId.values()).sort((a, b) => (a.parent ? 1 : 0) - (b.parent ? 1 : 0));
  }, [state, platformCtx, module, workspaceRows]);
  const roots = allForModule.filter(e => !e.parent);
  const isTwoLevel = module === "Facility";
  const lbl = (e) => !e ? "" : (isRTL && e.labelAr ? e.labelAr : e.label);

  /* Persist. For seed/platform entries the workspace layer stores a
     full copy (Q12 decision). Workspace entries are upserted in
     place. Dispatch first for optimistic UI; rollback on failure. */
  const persist = async (entry, isNew) => {
    const existing = workspaceRows.find(r => r.id === entry.id);
    const body = {
      action: "save_taxonomy_entry",
      ws_key: state.wsKey,
      entry,
      actor_id: state.currentUser?.id || "",
      actor_name: state.currentUser?.name || "",
    };
    if (!existing) dispatch({ type: "ADD_TASK_TAXONOMY_ENTRY", entry });
    else dispatch({ type: "UPDATE_TASK_TAXONOMY_ENTRY", id: entry.id, patch: entry });
    setSaving(true); setErrorMsg("");
    try {
      const r = await authFetch(`${API}/api/tasks`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErrorMsg(d.error || `HTTP ${r.status}`);
        /* Rollback optimistic dispatch */
        if (!existing) dispatch({ type: "REMOVE_TASK_TAXONOMY_ENTRY", id: entry.id });
      }
    } catch (e) { setErrorMsg(e.message); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (entry) => {
    if (entry.source !== "workspace") {
      setErrorMsg(isRTL ? "لا يمكن حذف الإعدادات الافتراضية — قم بتعطيلها" : "Can't delete defaults — disable instead.");
      return;
    }
    if (!window.confirm(`${isRTL ? "حذف" : "Delete"} "${lbl(entry)}"?`)) return;
    dispatch({ type: "REMOVE_TASK_TAXONOMY_ENTRY", id: entry.id });
    setSaving(true);
    try {
      const r = await authFetch(`${API}/api/tasks`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "delete_taxonomy_entry", ws_key: state.wsKey, id: entry.id }) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setErrorMsg(d.error || `HTTP ${r.status}`); }
    } catch (e) { setErrorMsg(e.message); }
    finally { setSaving(false); }
  };

  /* Build an updated entry from a seed/platform row + patch */
  const patchEntry = (entry, patch) => {
    const next = { ...entry, ...patch, updatedAt: new Date().toISOString() };
    /* If coming from seed/platform, strip the source tag (workspace
       rows don't carry it in storage). */
    delete next.source;
    persist(next, entry.source !== "workspace");
  };

  const addEntry = () => {
    const f = addForm;
    if (!f.label.trim() || !f.value.trim()) { setErrorMsg(isRTL ? "المفتاح والتسمية مطلوبان" : "Value + label required"); return; }
    const entry = {
      id: `tax_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      module,
      parent: showAdd.parentId || null,
      value: f.value.trim().toLowerCase().replace(/\s+/g, "_"),
      label: f.label.trim(),
      labelAr: f.labelAr.trim(),
      defaultPriority: f.defaultPriority || "medium",
      requiresApproval: !!f.requiresApproval,
      requiresExpense: !!f.requiresExpense,
      enabled: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    persist(entry, true);
    setShowAdd(null); setAddForm(null);
  };

  const badge = (src) => {
    const map = { seed: { l: isRTL ? "افتراضي" : "Default", c: "var(--g400)" }, platform: { l: isRTL ? "منصة" : "Platform", c: "var(--caccent,#C9A84C)" }, workspace: { l: isRTL ? "مساحة العمل" : "Workspace", c: "#22c55e" } };
    const b = map[src] || map.seed;
    return <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: b.c, padding: "2px 6px", border: `1px solid ${b.c}`, borderRadius: 4, marginInlineStart: 6 }}>{b.l}</span>;
  };

  const row = (e, indent = 0) => (
    <tr key={e.id} style={{ borderBottom: "1px solid var(--g100)", opacity: e.enabled === false ? 0.55 : 1 }}>
      <td style={{ padding: "8px 10px", paddingInlineStart: 10 + indent * 20 }}>
        <div style={{ color: "var(--ink)", fontWeight: indent ? 400 : 600 }}>{lbl(e)}{badge(e.source)}</div>
        <div style={{ fontSize: 11, color: "var(--g400)", fontFamily: "var(--fm,monospace)" }}>{e.value}</div>
      </td>
      <td style={{ padding: "4px 6px" }}>
        <input className="fi" style={{ width: "100%", fontSize: 12 }} defaultValue={e.label} onBlur={ev => ev.target.value !== e.label && patchEntry(e, { label: ev.target.value })} />
      </td>
      <td style={{ padding: "4px 6px" }}>
        <input className="fi" style={{ width: "100%", fontSize: 12 }} dir="rtl" defaultValue={e.labelAr || ""} onBlur={ev => ev.target.value !== (e.labelAr || "") && patchEntry(e, { labelAr: ev.target.value })} />
      </td>
      <td style={{ padding: "4px 6px" }}>
        <select className="fs" style={{ fontSize: 12 }} value={e.defaultPriority || "medium"} onChange={ev => patchEntry(e, { defaultPriority: ev.target.value })}>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </td>
      <td style={{ padding: "4px 6px", textAlign: "center" }}>
        <input type="checkbox" checked={!!e.requiresApproval} onChange={ev => patchEntry(e, { requiresApproval: ev.target.checked })} />
      </td>
      <td style={{ padding: "4px 6px", textAlign: "center" }}>
        <input type="checkbox" checked={!!e.requiresExpense} onChange={ev => patchEntry(e, { requiresExpense: ev.target.checked })} />
      </td>
      <td style={{ padding: "4px 6px", textAlign: "center" }}>
        <input type="checkbox" checked={e.enabled !== false} onChange={ev => patchEntry(e, { enabled: ev.target.checked })} />
      </td>
      <td style={{ padding: "4px 6px", minWidth: 180 }}>
        <select className="fs" style={{ fontSize: 12, width: "100%" }} value={e.defaultExpenseCatId || ""} onChange={ev => {
          const catId = ev.target.value || null;
          /* Clear sub when the parent cat changes so an orphan sub
             doesn't stick around. */
          const patch = { defaultExpenseCatId: catId };
          if (!catId || !subCatsOf(catId).some(s => s.id === e.defaultExpenseSubId)) patch.defaultExpenseSubId = null;
          patchEntry(e, patch);
        }}>
          <option value="">— {isRTL ? "بدون" : "none"} —</option>
          {expenseCats.map(c => <option key={c.id} value={c.id}>{isRTL && c.nameAr ? c.nameAr : c.name}</option>)}
        </select>
        {e.defaultExpenseCatId && subCatsOf(e.defaultExpenseCatId).length > 0 && (
          <select className="fs" style={{ fontSize: 12, width: "100%", marginTop: 4 }} value={e.defaultExpenseSubId || ""} onChange={ev => patchEntry(e, { defaultExpenseSubId: ev.target.value || null })}>
            <option value="">— {isRTL ? "أي" : "any"} —</option>
            {subCatsOf(e.defaultExpenseCatId).map(s => <option key={s.id} value={s.id}>{isRTL && s.nameAr ? s.nameAr : s.name}</option>)}
          </select>
        )}
      </td>
      <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>
        {isTwoLevel && !e.parent && (
          <button className="btn btn-s btn-xs" style={{ fontSize: 10, marginInlineEnd: 4 }} onClick={() => { setShowAdd({ parentId: e.id }); setAddForm({ value: "", label: "", labelAr: "", defaultPriority: "medium", requiresApproval: false, requiresExpense: false }); }}>+ {isRTL ? "نوع" : "Type"}</button>
        )}
        {e.source === "workspace" && (
          <button className="btn btn-d btn-xs" style={{ fontSize: 10 }} onClick={() => deleteEntry(e)} disabled={saving}>{isRTL ? "حذف" : "Del"}</button>
        )}
      </td>
    </tr>
  );

  return (
    <div>
      {errorMsg && <div className="p-notice" style={{ marginBottom: 10, color: "var(--red)" }}>{errorMsg}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "var(--g400)", lineHeight: 1.55, maxWidth: 560 }}>
          {isRTL
            ? "تجاوز الإعدادات الافتراضية المنصة أو أضف إدخالات خاصة بمساحة العمل. تظهر التغييرات فوراً في نموذج إنشاء المهمة."
            : "Override platform defaults or add workspace-only entries. Changes appear in the task-create dropdowns immediately."}
        </div>
        <button className="btn btn-p btn-sm" style={{ fontSize: 12 }} onClick={() => { setShowAdd({ parentId: null }); setAddForm({ value: "", label: "", labelAr: "", defaultPriority: "medium", requiresApproval: false, requiresExpense: false }); }}>+ {isTwoLevel ? (isRTL ? "فئة" : "Category") : (isRTL ? "إدخال" : "Entry")}</button>
      </div>

      <div className="p-card" style={{ padding: 0, overflow: "auto" }}>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid var(--g200)", textAlign: "start" }}>
            {[isRTL ? "الاسم" : "Name", isRTL ? "التسمية" : "Label", isRTL ? "التسمية ع" : "Label AR", isRTL ? "الأولوية" : "Priority", isRTL ? "موافقة" : "Appr.", isRTL ? "مصروف" : "Exp.", isRTL ? "مفعل" : "On", isRTL ? "فئة المصروف" : "Expense Cat", ""].map((h, i) => (
              <th key={i} style={{ padding: "8px 10px", color: "var(--g400)", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {isTwoLevel
              ? roots.flatMap(r => [row(r, 0), ...allForModule.filter(c => c.parent === r.id).map(c => row(c, 1))])
              : allForModule.map(e => row(e, 0))}
          </tbody>
        </table>
        {!allForModule.length && <div style={{ padding: 24, textAlign: "center", color: "var(--g400)", fontSize: 13 }}>{isRTL ? "لا توجد إدخالات" : "No entries."}</div>}
      </div>

      {showAdd && addForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setShowAdd(null); setAddForm(null); }}>
          <div style={{ background: "var(--w,#fff)", borderRadius: 16, width: "100%", maxWidth: 520, boxShadow: "0 24px 48px rgba(0,0,0,.3)" }} onClick={ev => ev.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--g100)", fontSize: 16, fontWeight: 600, color: "var(--ink)" }}>{isRTL ? "إدخال جديد" : "New Entry"} — {module}{showAdd.parentId && <span style={{ color: "var(--g400)", fontWeight: 400, fontSize: 13 }}> {isRTL ? "تحت" : "under"} {lbl(allForModule.find(v => v.id === showAdd.parentId))}</span>}</div>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="fr">
                <div className="fg" style={{ flex: 1 }}><label className="fl">{isRTL ? "المفتاح" : "Value (key)"}</label><input className="fi" value={addForm.value} onChange={ev => setAddForm(p => ({ ...p, value: ev.target.value }))} placeholder="snake_case" autoFocus /></div>
                <div className="fg" style={{ flex: 1 }}><label className="fl">{isRTL ? "الأولوية" : "Priority"}</label><select className="fs" value={addForm.defaultPriority} onChange={ev => setAddForm(p => ({ ...p, defaultPriority: ev.target.value }))}>{PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
              </div>
              <div className="fr">
                <div className="fg" style={{ flex: 1 }}><label className="fl">{isRTL ? "التسمية" : "Label"}</label><input className="fi" value={addForm.label} onChange={ev => setAddForm(p => ({ ...p, label: ev.target.value }))} /></div>
                <div className="fg" style={{ flex: 1 }}><label className="fl">{isRTL ? "التسمية ع" : "Label (AR)"}</label><input className="fi" dir="rtl" value={addForm.labelAr} onChange={ev => setAddForm(p => ({ ...p, labelAr: ev.target.value }))} /></div>
              </div>
              <div style={{ display: "flex", gap: 20, fontSize: 13 }}>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}><input type="checkbox" checked={addForm.requiresApproval} onChange={ev => setAddForm(p => ({ ...p, requiresApproval: ev.target.checked }))} /> {isRTL ? "يتطلب موافقة" : "Requires Approval"}</label>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}><input type="checkbox" checked={addForm.requiresExpense} onChange={ev => setAddForm(p => ({ ...p, requiresExpense: ev.target.checked }))} /> {isRTL ? "يتطلب مصروف" : "Requires Expense"}</label>
              </div>
            </div>
            <div style={{ padding: "14px 20px", borderTop: "1px solid var(--g100)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-g btn-sm" onClick={() => { setShowAdd(null); setAddForm(null); }}>{t?.cancel || (isRTL ? "إلغاء" : "Cancel")}</button>
              <button className="btn btn-p btn-sm" onClick={addEntry} disabled={saving}>{saving ? (isRTL ? "جارٍ الحفظ..." : "Saving…") : (isRTL ? "إضافة" : "Add")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
